import { Data$, Notification, RemoteData } from '@lycoris-nubila/ngx-lycoris';
import { Observable } from 'rxjs';

import { CommandEvent } from 'application/common/model/command-event';
import { NotificationDomain } from 'application/common/model/notification-domain';
import { UserModuleStatisticOutput } from 'infrastructure/graphql/content/user/model/user-module-statistic.output';
import { UserModuleOutput } from 'infrastructure/graphql/content/user/model/user-module.output';
import { UserPhaseStatsOutput } from 'infrastructure/graphql/content/user/model/user-phase-stats.output';
import { MessagingUserModulePushOutput } from 'infrastructure/graphql/messaging/user/model/messaging-user-module-push.output';
import { AssociateCoachWithCoacheeCmd } from 'user/command/associate-coach-with-coachee.cmd';
import { ConnectToUserNetworkCmd } from 'user/command/connect-to-user-network.cmd';
import { CreateUserCmd } from 'user/command/create-user.cmd';
import { GenerateUserCVCmd } from 'user/command/generate-user-cv.cmd';
import { MarkUserModuleDescriptionAsVisualizedCmd } from 'user/command/mark-user-module-description-as-visualized.cmd';
import { CreateUserModuleAnswersDocumentCmd } from 'user/command/module/answer/create-user-module-answers-document.cmd';
import { DeleteUserModuleAnswersCmd } from 'user/command/module/answer/delete-user-module-answers.cmd';
import { GenerateUserModuleAnswersCmd } from 'user/command/module/answer/generate-user-module-answers.cmd';
import { ResendUserModulePushCmd } from 'user/command/module/push/resend-user-module-push.cmd';
import { UpdateUserModuleCoachCommentCmd } from 'user/command/module/update-user-module-coach-comment.cmd';
import { UpdateUserModuleRatingCmd } from 'user/command/module/update-user-module-rating.cmd';
import { CreateUserNoteCmd } from 'user/command/note/create-user-note.cmd';
import { ReconnectToUserNetworkCmd } from 'user/command/reconnect-to-user-network.cmd';
import { SaveUserModuleSpentDurationCmd } from 'user/command/save-user-module-spent-duration.cmd';
import { SynchronizeUserNetworkSelectedCalendarsCmd } from 'user/command/synchronize-user-network-selected-calendars.cmd';
import { UpdateCoacheeObserversCmd } from 'user/command/update-coachee-observers.cmd';
import { UpdateUserSettingsCmd } from 'user/command/update-user-settings.cmd';
import { Auth0ConnectionOutput } from 'user/model/auth0-connection.output';
import { ExternalCalendarInformationOutput } from 'user/model/external-calendar-information.output';
import { StatisticsUserOutput } from 'user/model/statistics-user.output';
import { UserAvailableSlotOutput } from 'user/model/user-available-slot.output';
import { UserMfaEngageOutput } from 'user/model/user-mfa-engage.output';
import { UserNetworkState } from 'user/model/user-network.state';
import { UserNetworkType } from 'user/model/user-network.type';
import { IUserNote } from 'user/note/user-note';
import { FetchOwnedUserModulePushesQuery } from 'user/query/fetch-owned-user-module-pushes.query';
import { FetchUserModulePushesQuery } from 'user/query/module/push/fetch-user-module-pushes.query';
import { ObserveSearchedUserCoachNotesQuery } from 'user/query/note/observe-searched-user-coach-notes.query';
import { IUserModuleSuggestion } from 'user/suggestion/user-module-suggestion';
import { IUser } from 'user/user';
import { IUserEmailModel } from 'user/user-email-model';
import { IUserModule } from 'user/user-module';
import { IUserModuleAnswer } from 'user/user-module-answer';
import { UserModulePush } from 'user/user-module-push';
import { IUserSettings } from 'user/user-settings';

import { AddModuleToUserPlanCmd } from '../command/module/add-module-to-user-plan.cmd';
import { SaveUserAnswersCmd } from '../command/module/answer/save-user-answers.cmd';
import { DeleteModuleFromUserPlanCmd } from '../command/module/delete-module-from-user-plan.cmd';
import { UpdateUserModuleCmd } from '../command/module/update-user-module.cmd';
import { UpdateCoachCmd } from '../command/update-coach.cmd';
import { UpdateUserAvailabilitiesCmd } from '../command/update-user-availabilities.cmd';
import { UpdateUserBiographyCmd } from '../command/update-user-biography.cmd';
import { UpdateUserCmd } from '../command/update-user.cmd';
import { FetchUserAvailableSlotsQuery } from '../query/fetch-user-available-slots.query';
import { ObserveUserAnswersByModulesIdQuery } from '../query/module/answer/observe-user-answers-by-modules-id.query';
import { ObserveUserAnswersByQuestionsIdQuery } from '../query/module/answer/observe-user-answers-by-questions-id.query';
import { ObserveSearchedCoachesIncludingPartnersQuery } from '../query/observe-searched-coaches-including-partners.query';

export abstract class UserApplicationPort {
	public abstract commandHandled$: Observable<CommandEvent<UserApplicationPort>>;

	public abstract mustAnswerOnBoardingModule(): Observable<boolean>;

	public abstract mustAnswerFirstCoachingPlanModule(): Observable<boolean>;

	public abstract fetchStatisticsUser(): Observable<StatisticsUserOutput>;

	public abstract createUser(cmd: CreateUserCmd): Promise<void>;

	public abstract observeUserModuleSuggestions(): Observable<
		IUserModuleSuggestion[]
	>;

	public abstract markMoodDescriptionShown(): Observable<void | Error>;

	public abstract fetchUserModuleSuggestions(): Observable<void | Error>;

	public abstract observeUserModuleSuggestionsChanges(): Observable<
		Notification<NotificationDomain, string>
	>;

	public abstract updateUserGrantNewsLetter(
		grantNewsLetter: boolean
	): Observable<void>;

	public abstract observeUserPlanChanges(): Observable<
		Notification<NotificationDomain, null>
	>;

	public abstract fetchUserModules(
		userId: string
	): Observable<UserModuleOutput[]>;

	public abstract fetchUserSettings(): Observable<IUserSettings>;

	public abstract addModuleToUserPlan(
		cmd: AddModuleToUserPlanCmd
	): Promise<void>;

	public abstract resendUserModulePush(
		cmd: ResendUserModulePushCmd
	): Promise<void>;

	public abstract associateUserWithCoach(coachId: string): Promise<void>;

	public abstract closeUserSession(): void;

	public abstract deleteUserModuleAnswers(
		cmd: DeleteUserModuleAnswersCmd
	): Promise<void>;

	public abstract deleteModuleFromUserPlan(
		cmd: DeleteModuleFromUserPlanCmd
	): Promise<void>;

	public abstract fetchUserAvailableSlots(
		query: FetchUserAvailableSlotsQuery
	): Promise<UserAvailableSlotOutput[]>;

	public abstract fetchUserNetworkInfo(
		auth0UserId: string
	): Promise<Auth0ConnectionOutput>;

	public abstract fetchUserPhasesStats(
		userId: string
	): Promise<UserPhaseStatsOutput[]>;

	public abstract disableUserMfa(): Observable<void>;

	public abstract observeSearchedCoachesIncludingPartners(
		query: ObserveSearchedCoachesIncludingPartnersQuery
	): Observable<RemoteData<IUser[]>>;

	public abstract fetchUser(): Observable<void | Error>;

	public abstract observeSessionUser(): Observable<IUser>;

	public abstract observeSessionUserId(): Observable<string>;

	public abstract observeUserAnswersByModulesId(
		query: ObserveUserAnswersByModulesIdQuery
	): Observable<RemoteData<IUserModuleAnswer[]>>;

	public abstract disassociateCoachWithCoachee(
		coacheeId: string
	): Observable<void>;

	public abstract observeUserAnswersByQuestionsId(
		query: ObserveUserAnswersByQuestionsIdQuery
	): Observable<RemoteData<IUserModuleAnswer[]>>;

	public abstract observeUserById(id: string): Data$<IUser>;

	public abstract observeUserByIds(ids: string[]): Data$<IUser[]>;

	public abstract observeUserModules(
		userId: string
	): Observable<RemoteData<IUserModule[]>>;

	public abstract fetchModulesStatistic(): Observable<
		UserModuleStatisticOutput[]
	>;

	public abstract fetchUserConnectedDaysCount(): Observable<number>;

	public abstract fetchUserModulePushes(
		query: FetchUserModulePushesQuery
	): Observable<UserModulePush[]>;

	public abstract observeCoachCoachees(): Observable<RemoteData<IUser[]>>;

	public abstract saveUserAnswers(cmd: SaveUserAnswersCmd): Promise<void>;

	public abstract updateCoach(cmd: UpdateCoachCmd): Promise<void>;

	public abstract updateCoachBiography(
		cmd: UpdateUserBiographyCmd
	): Promise<void>;

	public abstract updateUser(cmd: UpdateUserCmd): Promise<void>;

	public abstract updateUserAvailabilities(
		cmd: UpdateUserAvailabilitiesCmd
	): Promise<void>;

	public abstract updateUserAvatarUrl(file: File): Promise<void>;

	public abstract updateUserModule(cmd: UpdateUserModuleCmd): Promise<void>;

	public abstract updateUserModuleCoachComment(
		cmd: UpdateUserModuleCoachCommentCmd
	): Promise<void>;

	public abstract updateUserModuleRating(
		cmd: UpdateUserModuleRatingCmd
	): Promise<void>;

	public abstract updateUserQuote(quote: string): Promise<void>;

	public abstract updateUserLanguage(lang: string): Promise<IUser>;

	public abstract fetchUserEmailModels(): Observable<string[] | Error>;

	public abstract observeUserEmailModels(): Observable<IUserEmailModel[]>;

	public abstract createUserModuleAnswersDocument(
		cmd: CreateUserModuleAnswersDocumentCmd
	): Observable<void>;

	public abstract generateUserModuleAnswers(
		cmd: GenerateUserModuleAnswersCmd
	): Observable<string>;

	public abstract updateCoacheeObservers(
		cmd: UpdateCoacheeObserversCmd
	): Observable<void>;

	public abstract createUserNote(cmd: CreateUserNoteCmd): Promise<void>;

	public abstract deleteUserNote(noteId: string): Promise<void>;

	public abstract associateCoachWithCoachee(
		cmd: AssociateCoachWithCoacheeCmd
	): Observable<void>;

	public abstract observeCoacheesWithoutCoach(ids: string[]): Data$<IUser[]>;

	public abstract updateUserSettings(
		cmd: UpdateUserSettingsCmd
	): Observable<IUserSettings>;

	public abstract generateUserMfa(): Observable<UserMfaEngageOutput>;

	public abstract validateUserMfa(code: string): Observable<void>;

	public abstract isUserNewsletterEnabled(): Observable<boolean>;

	public abstract generateUserCV(cmd: GenerateUserCVCmd): Observable<string>;

	public abstract saveUsers(ids: string[]): Promise<void>;

	public abstract observeSessionOrganizationId(): Observable<string>;

	public abstract SearchUserCoachNotes(
		query: ObserveSearchedUserCoachNotesQuery
	): Observable<string[] | Error>;

	public abstract observeNotes(ids: string[]): Observable<IUserNote[]>;

	public abstract saveUserModuleSpentDuration(
		cmd: SaveUserModuleSpentDurationCmd
	): Observable<void>;

	public abstract fetchUserDaysUntilNextLevelUpCount(): Observable<number>;

	public abstract reconnectToUserNetwork(
		cmd: ReconnectToUserNetworkCmd
	): Observable<void>;

	public abstract disconnectFromUserNetwork(
		networkType: UserNetworkType
	): Observable<void>;

	public abstract userNetworkState(
		networkType: UserNetworkType
	): Observable<UserNetworkState>;

	public abstract synchronizeUserNetworkSelectedCalendars(
		cmd: SynchronizeUserNetworkSelectedCalendarsCmd
	): Observable<void>;

	public abstract userExternalCalendar(
		networkType: UserNetworkType
	): Observable<ExternalCalendarInformationOutput[]>;

	public abstract connectToUserNetwork(
		cmd: ConnectToUserNetworkCmd
	): Observable<void>;

	public abstract observeWebsocketChanges(): Observable<boolean>;

	public abstract markUserModuleDescriptionAsVisualized(
		cmd: MarkUserModuleDescriptionAsVisualizedCmd
	): Observable<void>;

	public abstract countUserModulePushesByStatusApiQuery(
		userId: string
	): Observable<{
		[key: string]: number;
	}>;

	public abstract observeUserModuleMetadataGenerated(): Observable<
		Notification<NotificationDomain, string>
	>;

	public abstract observeUserModulePushUpdated(): Observable<
		Notification<NotificationDomain, string>
	>;

	public abstract generateUserPushModuleAnswersSynthesis(
		coacheeId: string
	): Observable<void>;

	public abstract markUserHelpConversationAsShown();

	public abstract ownedUserModulePushes(
		query: FetchOwnedUserModulePushesQuery
	): Observable<MessagingUserModulePushOutput[]>;
	public abstract observeUserStatisticsChanges(): Observable<
		Notification<NotificationDomain, null>
	>;
}
